/**
 * Contractor community table - the Pool tab's "who owes what" picture.
 *
 * Mirrors the partner network table's expandable parent/child pattern:
 * contractor GROUPS (parent + sub-locations) roll up Registrations (paid,
 * prior yr) / Due / Carryover / Done / Scheduled and the go-back-and-fix
 * load (failed + open punch items). Behind-schedule groups arrive first
 * (server-sorted). Row selection scopes Smart Schedule.
 */

import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ChevronDown, ChevronRight, Layers, Wrench } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import {
  type ContractorSummary,
  type ContractorSummaryGroup,
  type ContractorSummaryRow,
} from '@/admin/hooks/use-inspections'

function StatusBadge({ row }: { row: ContractorSummaryRow }) {
  if (row.behind_schedule) {
    return <Badge variant="destructive">Behind</Badge>
  }
  if (row.due > 0 && row.remaining === 0) {
    return <Badge variant="secondary">On track</Badge>
  }
  return <span className="text-[var(--ink-3)]">-</span>
}

function IssuesCell({ row }: { row: ContractorSummaryRow }) {
  const total = row.issues.failed + row.issues.open_punch_items
  if (!total) return <span className="text-[var(--ink-3)]">-</span>
  return (
    <span
      className="inline-flex items-center gap-1 text-[var(--status-warn,#92400e)]"
      title={`${row.issues.failed} failed inspection(s), ${row.issues.open_punch_items} open punch-list item(s)`}
    >
      <Wrench size={12} />
      {total}
    </span>
  )
}

function NumCell({ value, strong }: { value: number; strong?: boolean }) {
  if (!value) return <span className="text-[var(--ink-3)]">-</span>
  return (
    <span className={strong ? 'font-medium text-[var(--ink)]' : undefined}>
      {value}
    </span>
  )
}

export function ContractorSummaryTable({
  summary,
  isLoading,
  isFetching,
  isError,
  onRetry,
  selected,
  onToggle,
  onToggleAll,
  onOpenContractor,
}: {
  summary: ContractorSummary | undefined
  isLoading: boolean
  /** React Query background-fetch state - pairs with `isLoading` so the
   * empty state never flashes over a cached-empty-then-refetch window. */
  isFetching?: boolean
  isError?: boolean
  onRetry?: () => void
  /** Selected contractor org uuids (children count individually). */
  selected: Set<string>
  onToggle: (uuids: string[], next: boolean) => void
  onToggleAll: (next: boolean) => void
  onOpenContractor?: (uuid: string, name: string) => void
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const allUuids = useMemo(() => {
    const out: string[] = []
    for (const g of summary?.results ?? []) {
      if (g.children.length) {
        out.push(...g.children.map((c) => c.organisation_uuid))
      }
      out.push(g.group.organisation_uuid)
    }
    return out
  }, [summary])

  const allSelected =
    allUuids.length > 0 && allUuids.every((u) => selected.has(u))

  const cell = 'px-3 py-2 text-xs text-[var(--ink-2)]'
  const numCell = `${cell} text-right tabular-nums`

  const toggleExpand = (uuid: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })

  const renderDataRow = (
    row: ContractorSummaryRow,
    opts: { child?: boolean; childUuids?: string[] } = {},
  ) => {
    const { child, childUuids } = opts
    const groupUuids = [row.organisation_uuid, ...(childUuids ?? [])]
    const isChecked = groupUuids.every((u) => selected.has(u))
    const hasChildren = (childUuids?.length ?? 0) > 0
    return (
      <tr
        key={row.organisation_uuid + (child ? ':child' : '')}
        className={
          child
            ? 'border-t border-[var(--rule)] bg-[var(--bg-light)]'
            : 'border-t border-[var(--rule)] hover:bg-[var(--bg-light)]'
        }
      >
        <td className={`${cell} ${child ? 'pl-10' : ''}`}>
          <span className="flex items-center gap-2">
            <Checkbox
              checked={isChecked}
              onCheckedChange={(next) => onToggle(groupUuids, next === true)}
              aria-label={`Select ${row.name}`}
            />
            {/* Only the child indent marker sits before the name. The
                expander used to live here too, which pushed every
                expandable contractor's name out of line with the rest -
                the eye reads that as two different kinds of row when it is
                just "this one has locations under it". */}
            {child && <span className="text-[var(--ink-3)]">&#8627;</span>}
            {row.partnership_uuid ? (
              <Link
                to={`/network/${row.partnership_uuid}`}
                className={`truncate text-left hover:underline ${
                  child
                    ? 'text-[var(--ink-2)]'
                    : 'font-medium text-[var(--ink)]'
                }`}
                title={`${row.name} - open partner record`}
              >
                {row.name}
              </Link>
            ) : (
              <span
                className={`truncate ${
                  child
                    ? 'text-[var(--ink-2)]'
                    : 'font-medium text-[var(--ink)]'
                }`}
                title={row.name}
              >
                {row.name}
              </span>
            )}
            {onOpenContractor && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() =>
                  onOpenContractor(row.organisation_uuid, row.name)
                }
                aria-label={`Browse ${row.name}'s eligible homes`}
                title="Browse eligible homes (stage by hand)"
                className="shrink-0 text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                <Layers size={12} />
              </Button>
            )}
            {/* The expander, after the stacking icon: both are "there is more
                behind this row" affordances, so they belong together, and
                the names stay aligned whether a row expands or not. */}
            {hasChildren && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => toggleExpand(row.organisation_uuid)}
                aria-label={
                  expanded.has(row.organisation_uuid)
                    ? 'Collapse locations'
                    : 'Expand locations'
                }
                title={
                  expanded.has(row.organisation_uuid)
                    ? 'Hide locations'
                    : 'Show locations'
                }
                className="shrink-0 text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                {expanded.has(row.organisation_uuid) ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </Button>
            )}
          </span>
        </td>
        <td
          className={numCell}
          title={
            row.target_pct
              ? `Due = ${parseFloat(row.target_pct)}% of ${row.registrations} prior-year paid registrations`
              : undefined
          }
        >
          <NumCell value={row.registrations} />
        </td>
        <td className={numCell}>
          {row.remaining > 0 ? (
            <Badge variant="destructive">{row.remaining}</Badge>
          ) : (
            <NumCell value={row.due} />
          )}
        </td>
        <td className={numCell}>
          {row.carryover > 0 ? (
            <span className="text-[var(--status-warn,#92400e)]">
              {row.carryover}
            </span>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          )}
        </td>
        <td className={numCell}>
          <NumCell value={row.done} />
        </td>
        <td className={numCell}>
          <NumCell value={row.scheduled + row.staged} />
        </td>
        <td className={numCell}>
          <IssuesCell row={row} />
        </td>
        <td className={cell}>
          <StatusBadge row={row} />
        </td>
      </tr>
    )
  }

  const columns: DataTableColumn<ContractorSummaryGroup>[] = [
    { id: 'contractor', header: 'Contractor' },
    { id: 'registrations', header: 'Registrations' },
    { id: 'due', header: 'Due' },
    { id: 'carryover', header: 'Carryover' },
    { id: 'done', header: 'Done' },
    { id: 'scheduled', header: 'Scheduled' },
    { id: 'issues', header: 'Issues' },
    { id: 'status', header: 'Status' },
  ]

  return (
    <div className="flex flex-col gap-2">
      <Label className="w-fit text-xs font-normal text-[var(--ink-3)]">
        <Checkbox
          checked={allSelected}
          onCheckedChange={(next) => onToggleAll(next === true)}
          aria-label="Select all contractors"
        />
        Select all contractors
      </Label>
      <DataTable<ContractorSummaryGroup>
        tableId="inspection-contractor-summary"
        columns={columns}
        data={summary?.results ?? []}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={onRetry}
        rowKey={(g) => g.group.organisation_uuid}
        emptyMessage="No partnered contractors yet."
        renderRow={(g) => (
          <Fragment key={g.group.organisation_uuid}>
            {renderDataRow(g.group, {
              childUuids: g.children.map((c) => c.organisation_uuid),
            })}
            {expanded.has(g.group.organisation_uuid) &&
              g.children.map((c) => renderDataRow(c, { child: true }))}
          </Fragment>
        )}
      />
    </div>
  )
}
